import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Star, ArrowRight } from "lucide-react";
import { Button } from "../components/Button";
import { SectionHead } from "../components/SectionHead";
import { Reveal, stagger, item } from "../lib/motion";
import { SERVICES, VALUE_EXCHANGE, PACKAGES } from "../data";

export default function Services() {
  useEffect(() => {
    document.title = "Services — PROSPEC Consultancy";
  }, []);

  return (
    <div className="pt-20">
      {/* INTRO */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <SectionHead
          eyebrow="Services"
          title="Planning and project controls, in full."
          intro="Nine disciplines that together give contractors complete command of the programme — from the first baseline to the final handover, and every delay in between."
        />
      </section>

      {/* SERVICE CARDS */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10"
        >
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              variants={item}
              data-testid={`service-card-${i}`}
              className="group bg-cream p-8 md:p-9 transition-all duration-300 hover:bg-sand hover:-translate-y-1"
            >
              <span className="eyebrow">{s.tag}</span>
              <h3 className="font-display text-2xl mt-4 mb-3 leading-tight">{s.title}</h3>
              <p className="text-slate leading-relaxed mb-6">{s.body}</p>
              <ul className="space-y-2.5 border-t border-ink/10 pt-5">
                {s.points.map((p) => (
                  <li key={p} className="flex gap-3 text-sm">
                    <Check size={16} className="text-teal shrink-0 mt-0.5" />
                    <span className="text-slate">{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* VALUE EXCHANGE TABLE */}
      <section className="bg-sand border-y border-ink/10">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <SectionHead
            eyebrow="Value exchange"
            title="What you get, in plain language."
            intro="Every service maps to a business outcome. Here is the translation."
          />
          <div className="mt-14 border border-ink/15 bg-cream">
            <div className="hidden md:grid grid-cols-2 bg-ink text-cream">
              <div className="p-5 font-mono text-xs uppercase tracking-[0.16em]">Service delivered</div>
              <div className="p-5 font-mono text-xs uppercase tracking-[0.16em] border-l border-cream/15">Business benefit to you</div>
            </div>
            {VALUE_EXCHANGE.map((row, i) => (
              <Reveal key={row.service} delay={i * 0.05}>
                <div className="grid md:grid-cols-2 border-t border-ink/10">
                  <div className="p-6 font-display text-lg text-ink flex items-center">{row.service}</div>
                  <div className="p-6 text-slate leading-relaxed md:border-l border-ink/10 flex items-center bg-cream md:bg-transparent">
                    {row.benefit}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT PACKAGES */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <SectionHead
          eyebrow="Engagement packages"
          title="Three ways to work with us."
          intro="Pricing is always tailored to the project and discussed after a free health-check — never off a rate card."
        />
        <div className="grid lg:grid-cols-3 gap-6 mt-16">
          {PACKAGES.map((pkg, i) => (
            <Reveal key={pkg.name} delay={i * 0.1}>
              <div
                data-testid={`package-${i}`}
                className={`relative h-full p-8 md:p-10 border transition-transform duration-300 hover:-translate-y-1 ${
                  pkg.recommended ? "border-gold border-2 bg-ink text-cream" : "border-ink/15 bg-cream"
                }`}
              >
                {pkg.recommended && (
                  <span className="absolute -top-3 left-8 bg-gold text-ink font-mono text-[0.65rem] uppercase tracking-[0.16em] px-3 py-1 flex items-center gap-1.5">
                    <Star size={12} fill="#101820" /> Recommended
                  </span>
                )}
                <h3 className={`font-display text-2xl mb-3 ${pkg.recommended ? "text-cream" : "text-ink"}`}>{pkg.name}</h3>
                <p className={`text-sm leading-relaxed mb-8 ${pkg.recommended ? "text-cream/70" : "text-slate"}`}>{pkg.tagline}</p>
                <ul className="space-y-3.5">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex gap-3">
                      <Check size={17} className={`shrink-0 mt-0.5 ${pkg.recommended ? "text-gold" : "text-teal"}`} />
                      <span className={`text-sm ${pkg.recommended ? "text-cream/85" : "text-slate"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <p className={`mt-8 font-mono text-xs ${pkg.recommended ? "text-gold" : "text-slate/70"}`}>
                  Pricing tailored after your health-check
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <Reveal>
          <div className="grain relative overflow-hidden bg-ink text-cream p-12 md:p-20 text-center">
            <div className="relative">
              <h2 className="font-display font-semibold text-3xl md:text-5xl text-cream max-w-3xl mx-auto leading-tight">
                Not sure which package fits your project?
              </h2>
              <p className="text-cream/70 text-lg mt-6 max-w-xl mx-auto">
                Book a free health-check and we will tell you exactly what your project needs — and
                what it doesn't.
              </p>
              <div className="mt-10">
                <Button to="/contact" data-testid="services-closing-cta">
                  Book a free health-check <ArrowRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
